import clienteAxios from "config/axios";
import { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import LineSpinner from "components/spinners/LineSpinner";
import LogoWhite from '../../assets/scss/black-dashboard-react/panther/logo-white.svg';
import { useT } from "i18n";

// Animaciones
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const StyledForm = styled.form`
  background: rgba(12,14,19, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  border: 1px solid rgba(227,168,43, 0.2);
  padding: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const TitleBox = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  
  h2 {
    font-size: 1.3rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    background: linear-gradient(135deg, #fff, #e3a82b);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
  
  h3 {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.5);
    
    strong {
      color: #e3a82b;
    }
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.6);
    
    i {
      margin-right: 0.5rem;
      color: #e3a82b;
    }
  }
  
  input, select {
    width: 100%;
    padding: 0.75rem 1rem;
    border-radius: 12px;
    border: 1px solid rgba(227,168,43, 0.3);
    background: rgba(12,14,19, 0.8);
    color: #fff;
    font-size: 0.9rem;
    transition: all 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: #e3a82b;
      box-shadow: 0 0 10px rgba(227,168,43, 0.3);
    }
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.3);
    }
  }
  
  select {
    cursor: pointer;
    
    option {
      background: #0c0e13;
    }
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  max-width: 300px;
  padding: 0.875rem;
  border-radius: 50px;
  border: none;
  background: #e3a82b;
  color: #14100a;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(227,168,43, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #0c0e13, #0c0e13);
  border-radius: 24px;
  border: 1px solid rgba(227,168,43, 0.3);
  max-width: 450px;
  width: 90%;
  padding: 2rem;
  text-align: center;
  
  img {
    width: 80px;
    height: auto;
    margin-bottom: 1rem;
  }
  
  h2 {
    font-size: 1.2rem;
    color: #e3a82b;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 0.5rem;
  }
`;

const ModalButton = styled.button`
  padding: 0.75rem 2rem;
  border-radius: 50px;
  border: none;
  background: #e3a82b;
  color: #14100a;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(227,168,43, 0.4);
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: 1px solid rgba(227,168,43, 0.3);
  border-radius: 50px;
  padding: 0.5rem 1rem;
  color: #e3a82b;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1.5rem;
  
  &:hover {
    background: rgba(227,168,43, 0.1);
    border-color: #e3a82b;
  }
  
  i {
    font-size: 0.9rem;
  }
`;

const FooterDecoration = styled.div`
  height: 4px;
  background: linear-gradient(90deg, #e3a82b, #e3a82b, #e3a82b);
  border-radius: 0 0 24px 24px;
  margin-top: 1rem;
`;

function Transfer({ onCancel }) {
  const { t, ts } = useT();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState("");
  const [transferData, setTransferData] = useState({
    card_from: "",
    card_to: "",
    amount: "",
    description: ""
  });
  
  const [tarjetas, setTarjetas] = useState([]);

  useEffect(() => {
    const loadCards = async () => {
      try {
        const response = await clienteAxios.post("/api/card/list");
        // Filtrar solo tarjetas activas para transferir
        const activeCards = response.data.data.filter(card => card.status === "APROBADO");
        setTarjetas(activeCards);
      } catch (error) {
        console.error("Error al cargar tarjetas:", error);
      }
    };
    loadCards();
  }, []);

  const handleChange = (e) => {
    setTransferData({
      ...transferData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!transferData.card_from) {
      setMessage(t("tarjeta.selOrigen"));
      setShowModal(true);
      return;
    }
    if (!transferData.card_to) {
      setMessage(t("tarjeta.selDestino"));
      setShowModal(true);
      return;
    }
    if (!transferData.amount || transferData.amount <= 0) {
      setMessage(t("tarjeta.montoValido"));
      setShowModal(true);
      return;
    }
    if (transferData.card_from === transferData.card_to) {
      setMessage(t("tarjeta.mismaTarjeta"));
      setShowModal(true);
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await clienteAxios.post("/api/card/transfer", transferData);
      if (response.data.success) {
        setMessage(t("tarjeta.transferOk"));
        setShowModal(true);
        // Limpiar formulario
        setTransferData({
          card_from: "",
          card_to: "",
          amount: "",
          description: ""
        });
      } else {
        setMessage(ts(response.data.message) || t("tarjeta.transferError"));
        setShowModal(true);
      }
    } catch (error) {
      setMessage(ts(error.response?.data?.message) || t("tarjeta.transferError"));
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setMessage("");
  };

  return (
    <>
      {onCancel && (
        <BackButton onClick={onCancel}>
          <i className="tim-icons icon-minimal-left" /> {t("tarjeta.volverListado")}
        </BackButton>
      )}
      
      <StyledForm onSubmit={handleSubmit}>
        <TitleBox>
          <h2>
            <i className="tim-icons icon-send mr-2" />
            {t("tarjeta.transferirTitulo")}
          </h2>
          <h3>www<strong>.panther7.</strong>com</h3>
        </TitleBox>

        <GridContainer>
          <InputGroup>
            <label><i className="tim-icons icon-credit-card" /> {t("tarjeta.lblOrigen")}</label>
            <select 
              name="card_from" 
              value={transferData.card_from} 
              onChange={handleChange}
              required
            >
              <option value="">{t("tarjeta.seleccioneTarjeta")}</option>
              {tarjetas.map((tarjeta) => (
                <option key={tarjeta.id} value={tarjeta.id}>
                  {tarjeta.card_model} - {tarjeta.number ? `****${tarjeta.number.slice(-4)}` : "****"} ({tarjeta.balance} USDT)
                </option>
              ))}
            </select>
          </InputGroup>

          <InputGroup>
            <label><i className="tim-icons icon-credit-card" /> {t("tarjeta.lblDestino")}</label>
            <select 
              name="card_to" 
              value={transferData.card_to} 
              onChange={handleChange}
              required
            >
              <option value="">{t("tarjeta.seleccioneTarjeta")}</option>
              {tarjetas.map((tarjeta) => (
                <option key={tarjeta.id} value={tarjeta.id}>
                  {tarjeta.card_model} - {tarjeta.number ? `****${tarjeta.number.slice(-4)}` : "****"}
                </option>
              ))}
            </select>
          </InputGroup>

          <InputGroup>
            <label><i className="tim-icons icon-coins" /> {t("tarjeta.lblMonto")}</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              name="amount"
              value={transferData.amount}
              onChange={handleChange}
              placeholder="0.00"
              required
            />
          </InputGroup>

          <InputGroup>
            <label><i className="tim-icons icon-notes" /> {t("tarjeta.lblDescripcion")}</label>
            <input
              type="text"
              name="description"
              value={transferData.description}
              onChange={handleChange}
              placeholder={t("tarjeta.phDescripcion")}
            />
          </InputGroup>
        </GridContainer>

        <ButtonContainer>
          <SubmitButton type="submit" disabled={loading}>
            {loading ? <LineSpinner /> : t("tarjeta.transferirBtn")}
          </SubmitButton>
        </ButtonContainer>
      </StyledForm>

      <FooterDecoration />

      {showModal && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <img src={LogoWhite} alt="Panther7" />
            <h2>{/éxito|success|sucesso/i.test(message) ? t("tarjeta.transferExitosa") : t("tarjeta.atencion")}</h2>
            <p>{message}</p>
            <ModalButton onClick={handleCloseModal}>{t("alerta.aceptar")}</ModalButton>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
}

export default Transfer;